// Dashboard game grid — iOS-style app-icon tiles.
//
// Source of truth: GET /api/me/games via useLobbyGames(). Renders the
// full 15-card catalogue with each tile's lock state pulled from the
// database (see migration 096 + admin Launch Matrix). Live games tap
// straight into their game page; locked games open the LockedGameModal
// (with the 5-tap dev easter egg inside) unless the user already has
// an unexpired dev-pin grant, in which case they tap straight in too.
//
// Tile visual contract: locked cards stay enticing — full-color art,
// no greyscale — but carry a top-right 🔒 chip with the unlock hint
// so the player understands why a tap doesn't launch the game.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '@baaligames/shared-ui/components/Icon';
import { useT } from '@baaligames/shared-ui/lib/i18n';
import { useToast } from '@baaligames/shared-ui/lib/toast';
import { useLobbyGames, type LobbyGame } from '../lib/lobbyGames';
import LockedGameModal from './LockedGameModal';

// A tile for a game that doesn't exist in the catalogue yet (e.g. Matka King).
// Rendered as a locked "Coming soon" tile in the strip.
export type SyntheticTile = { id: string; displayName: string; emoji: string; themeGradient?: string };

type Props = {
  onPlay?: (g: LobbyGame) => void;
  // 'full' (default) = the categorized, all-games lobby (the /games page).
  // 'strip' = a compact single horizontal row of the live games, for the
  // Home dashboard — so Home is a quick "jump back in" launcher, not a
  // second copy of the whole lobby. (UX Phase 2, 2026-08-08.)
  variant?: 'full' | 'strip';
  // strip only — show EXACTLY these game ids, in this order. Real games render
  // even if locked (so a chosen headline game always appears); ids with no
  // catalogue match fall back to the matching `synthetic` "coming soon" tile.
  only?: string[];
  // strip only — show the live games MINUS these ids (used for the "rest of
  // the games" row below the headline set).
  exclude?: string[];
  // definitions for `only` ids that aren't real games yet.
  synthetic?: SyntheticTile[];
};

export default function GameGrid({ onPlay, variant = 'full', only, exclude, synthetic }: Props) {
  const nav = useNavigate();
  const t = useT();
  const { push } = useToast();
  const { data, loading, error } = useLobbyGames();
  const [lockedModal, setLockedModal] = useState<LobbyGame | null>(null);
  // "Coming soon" (locked games) starts collapsed so playable games are
  // never pushed below the fold (2026-08-06 UX sweep).
  const [showSoon, setShowSoon] = useState(false);

  if (loading) {
    return <SkeletonGrid />;
  }
  if (error || !data) {
    return (
      <div className="text-[13px] text-rose-700 dark:text-rose-300 px-2">
        Couldn't load the game catalogue. {error || ''}
      </div>
    );
  }

  const grants = new Set(data.myDevGrants);
  // Live-first: a game is "playable now" if it isn't locked, or the user
  // holds a dev grant for it. Everything else is "coming soon". Filter
  // preserves the backend sortOrder within each bucket.
  const live = data.games.filter((g) => !g.isLocked || grants.has(g.id));
  const locked = data.games.filter((g) => g.isLocked && !grants.has(g.id));

  // Owner, 2026-08-11 — the lobby is grouped the way the BUSINESS works, in a
  // FIXED order, not by first-appearance of a loose descriptive tag:
  //
  //   Match Odds → real fixtures priced by the exchange
  //   Jackpot    → ONE result shared by every player in the round
  //   Casino     → house games played alone
  //   Versus     → player-vs-player boards
  //
  // The category lives in `games.tag` (display-only — the money taxonomy is
  // `rebate_category` and is untouched), so re-categorising a game, or slotting
  // a new one in, is a DB change rather than a code change. An unrecognised tag
  // falls into a trailing "More" section so a game is never silently dropped.
  //
  // Each category keeps ITS OWN games — playable first, then locked — instead of
  // exiling every locked game to one shared drawer: Matka King must be visible
  // inside Jackpot even though it is not built yet (owner's explicit ask).
  const CATEGORY_ORDER = ['Match Odds', 'Jackpot', 'Casino', 'Versus'];
  const MORE = 'More';
  const byCategory = new Map<string, LobbyGame[]>();
  for (const g of data.games) {
    const raw = (g.tag && g.tag.trim()) || MORE;
    const key = CATEGORY_ORDER.includes(raw) ? raw : MORE;
    if (!byCategory.has(key)) byCategory.set(key, []);
    byCategory.get(key)!.push(g);
  }
  const sections = [...CATEGORY_ORDER, MORE]
    .filter((c) => (byCategory.get(c) || []).length > 0)
    .map((c) => {
      // Order INSIDE a category is purely the curated `sortOrder`.
      // The catalogue query sorts `is_locked ASC, sort_order ASC` — lock state
      // outranks sort_order — which pushed the locked Matka King behind Lucky 7.
      // The owner wants Matka King leading Jackpot, so re-sort here on
      // sortOrder alone. Left the shared query untouched: other consumers still
      // get its "unlocked first" behaviour. Ordering is data, not a UI rule.
      const games = [...byCategory.get(c)!].sort((a, z) => a.sortOrder - z.sortOrder);
      return { label: c, games };
    });

  function handleTap(g: LobbyGame) {
    onPlay?.(g);
    // Live game OR user already has a dev grant → straight into gameplay.
    if (!g.isLocked || grants.has(g.id)) {
      nav(`/games/${g.id}`);
      return;
    }
    // Locked + no grant → modal with the level-3 message and the
    // 5-tap easter egg.
    setLockedModal(g);
  }

  // Compact strip (Home): one horizontal row of games.
  //   - `only`   → exactly those ids, in order (real games incl. locked, or a
  //                synthetic "coming soon" tile). Used for the headline Top Games.
  //   - `exclude`→ the live games minus those ids. Used for the "rest" row.
  //   - neither  → the live games (the original behaviour).
  if (variant === 'strip') {
    type StripItem = { real?: LobbyGame; syn?: SyntheticTile };
    let items: StripItem[];
    if (only && only.length) {
      items = only
        .map((id): StripItem | null => {
          const g = data.games.find((x) => x.id === id);
          if (g) return { real: g };
          const s = synthetic?.find((x) => x.id === id);
          return s ? { syn: s } : null;
        })
        .filter((x): x is StripItem => x !== null);
    } else {
      const base = exclude && exclude.length ? live.filter((g) => !exclude.includes(g.id)) : live;
      items = base.map((g) => ({ real: g }));
    }
    return (
      <>
        <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1
                        [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {items.map((it) => (
            <div key={it.real ? it.real.id : it.syn!.id} className="w-[76px] sm:w-[84px] shrink-0">
              {it.real
                ? <GameTile game={it.real} hasDevGrant={grants.has(it.real.id)} onTap={() => handleTap(it.real!)} />
                : <ComingSoonTile syn={it.syn!} onTap={() => push('info', `${it.syn!.displayName} — ${t('home.comingSoon')}`)} />}
            </div>
          ))}
        </div>
        {lockedModal && (
          <LockedGameModal game={lockedModal} onClose={() => setLockedModal(null)} />
        )}
      </>
    );
  }

  return (
    <>
      {sections.map((sec, i) => (
        <section key={sec.label} className={i === 0 ? '' : 'mt-5'}>
          <h3 className="mb-2 px-0.5 text-[11px] font-bold uppercase tracking-wider
                         text-arena-500 dark:text-neon-200/60">
            {sec.label}
          </h3>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-3 gap-y-4">
            {sec.games.map((g) => (
              <GameTile
                key={g.id}
                game={g}
                hasDevGrant={grants.has(g.id)}
                onTap={() => handleTap(g)}
              />
            ))}
          </div>
        </section>
      ))}


      {lockedModal && (
        <LockedGameModal game={lockedModal} onClose={() => setLockedModal(null)} />
      )}
    </>
  );
}

// ─── Tile ──────────────────────────────────────────────────────────

function GameTile({
  game, hasDevGrant, onTap,
}: {
  game: LobbyGame;
  hasDevGrant: boolean;
  onTap: () => void;
}) {
  const showLock = game.isLocked && !hasDevGrant;
  // Layered tile composition (each layer is absolutely positioned so
  // stacking is explicit, no flex-item collapse surprises):
  //   1. Gradient background (always there as a backup if the logo
  //      image is missing or has transparency).
  //   2. Logo.png artwork (shown on EVERY tile when the file exists —
  //      including locked games, so each card still reads as "the
  //      <game> tile" rather than a generic emoji placeholder).
  //   3. Emoji fallback — only rendered when the logo image is absent
  //      so the tile is never empty.
  //   4. Dark filter veil for locked tiles — sits over the artwork to
  //      visually communicate "in the shadows / not yet available".
  //   5. Lock chip + unlock-hint band (or DEV chip for dev grants).
  return (
    <div className="flex flex-col items-center">
      <button
        onClick={onTap}
        aria-label={showLock ? `Locked · ${game.displayName}` : `Play ${game.displayName}`}
        aria-disabled={showLock || undefined}
        className="tile aspect-square w-full !rounded-2xl relative overflow-hidden"
      >
        {/* L1 — gradient base. */}
        <div
          className={`absolute inset-0 ${game.themeGradient ? `bg-gradient-to-br ${game.themeGradient}` : ''}`}
          aria-hidden
        />

        {/* L2 — logo artwork. Always renders when the file exists
            (live AND locked games). The dark-veil overlay below
            shades it for locked tiles. */}
        {game.logoPath && (
          <img
            src={game.logoPath}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          />
        )}

        {/* L3 — emoji fallback (no logo file). */}
        {!game.logoPath && (
          <span
            className="absolute inset-0 grid place-items-center text-5xl sm:text-6xl drop-shadow"
            aria-hidden
          >
            {game.themeEmoji || '🎮'}
          </span>
        )}

        {/* L4 — dark filter veil for locked games. ~55% black + slight
            saturation drop reads as "shaded, not yet available" while
            keeping the artwork recognisable underneath. */}
        {showLock && (
          <div
            className="absolute inset-0 bg-black/55"
            style={{ backdropFilter: 'saturate(0.65)' }}
            aria-hidden
          />
        )}

        {/* L5 — lock chip (locked + no grant) OR DEV chip (granted). */}
        {showLock && (
          <>
            <span
              className="absolute top-1.5 right-1.5 z-10
                         inline-flex items-center gap-0.5 px-1.5 py-0.5
                         rounded-full text-[9px] font-bold uppercase tracking-wider
                         bg-black/70 text-white
                         ring-1 ring-inset ring-white/20
                         shadow-[0_2px_6px_rgba(0,0,0,0.45)]"
              aria-hidden
            >
              🔒
            </span>
            <span
              className="absolute bottom-0 inset-x-0 z-10 px-1.5 py-1
                         text-[11px] font-semibold text-center text-white
                         bg-gradient-to-t from-black/85 to-transparent
                         truncate"
            >
              {game.unlockHint}
            </span>
          </>
        )}
        {hasDevGrant && game.isLocked && (
          <span
            className="absolute top-1.5 right-1.5 z-10
                       inline-flex items-center gap-0.5 px-1.5 py-0.5
                       rounded-full text-[9px] font-bold uppercase tracking-wider
                       bg-emerald-500/85 text-white
                       ring-1 ring-inset ring-white/15"
            aria-label="Developer access granted"
          >
            DEV
          </span>
        )}
      </button>
      <div className="mt-1.5 w-full text-center text-[11px] sm:text-xs font-semibold text-neutral-800 dark:text-neutral-100 truncate">
        {game.displayName}
      </div>
    </div>
  );
}

// ─── Coming-soon tile (synthetic, not-yet-real game) ───────────────
// Same visual language as a locked GameTile (art + dark veil + 🔒 + band),
// but it never navigates — a tap just shows a "coming soon" toast. Used for
// headline games that don't exist in the catalogue yet (e.g. Matka King).
function ComingSoonTile({ syn, onTap }: { syn: SyntheticTile; onTap: () => void }) {
  const t = useT();
  return (
    <div className="flex flex-col items-center">
      <button
        onClick={onTap}
        aria-label={`${syn.displayName} · ${t('home.comingSoon')}`}
        aria-disabled
        className="tile aspect-square w-full !rounded-2xl relative overflow-hidden"
      >
        <div className={`absolute inset-0 bg-gradient-to-br ${syn.themeGradient || 'from-amber-400 to-yellow-600'}`} aria-hidden />
        <span className="absolute inset-0 grid place-items-center text-5xl sm:text-6xl drop-shadow" aria-hidden>
          {syn.emoji}
        </span>
        <div className="absolute inset-0 bg-black/50" style={{ backdropFilter: 'saturate(0.65)' }} aria-hidden />
        <span
          className="absolute top-1.5 right-1.5 z-10 inline-flex items-center px-1.5 py-0.5
                     rounded-full text-[9px] font-bold uppercase tracking-wider
                     bg-black/70 text-white ring-1 ring-inset ring-white/20
                     shadow-[0_2px_6px_rgba(0,0,0,0.45)]"
          aria-hidden
        >
          🔒
        </span>
        <span
          className="absolute bottom-0 inset-x-0 z-10 px-1.5 py-1 text-[11px] font-semibold
                     text-center text-white bg-gradient-to-t from-black/85 to-transparent truncate"
        >
          {t('home.comingSoon')}
        </span>
      </button>
      <div className="mt-1.5 w-full text-center text-[11px] sm:text-xs font-semibold text-neutral-800 dark:text-neutral-100 truncate">
        {syn.displayName}
      </div>
    </div>
  );
}

// ─── Skeleton (loading placeholder) ────────────────────────────────

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-3 gap-y-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex flex-col items-center">
          <div className="tile aspect-square w-full !rounded-2xl bg-arena-200/60 dark:bg-arena-700/40 animate-pulse" />
          <div className="mt-1.5 h-3 w-2/3 rounded bg-arena-200/60 dark:bg-arena-700/40 animate-pulse" />
        </div>
      ))}
    </div>
  );
}
